import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { updateProfile, getProfile } from "@/api/profile";
import { Card, SectionLabel } from "@/components/ui/Card";
import { SUPPORTED_LANGUAGES, type AppLanguage } from "@/i18n";

const LANG_LABELS: Record<AppLanguage, string> = {
  en: "English",
  ru: "Русский",
  uk: "Українська",
  es: "Español",
  de: "Deutsch",
  fr: "Français",
  it: "Italiano",
  pt: "Português",
  sv: "Svenska",
  da: "Dansk",
};

export function LanguageSection() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const profileQ = useQuery({ queryKey: ["profile"], queryFn: getProfile });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const currentLang = (profileQ.data?.language ?? "ru") as AppLanguage;

  async function handleChange(lang: AppLanguage) {
    if (lang === currentLang) return;
    setSaving(true);
    setToast(null);
    try {
      await updateProfile({ language: lang });
      i18n.changeLanguage(lang);
      localStorage.setItem("mavinty_language", lang);
      await qc.invalidateQueries({ queryKey: ["profile"] });
      setToast(t("settings.language_changed", { lang: LANG_LABELS[lang] }));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <SectionLabel>{t("settings.language")}</SectionLabel>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <button
            key={lang}
            type="button"
            disabled={saving}
            onClick={() => handleChange(lang)}
            className={`rounded-lg border px-3 py-2 text-center text-[13px] transition-colors ${
              currentLang === lang
                ? "border-amber bg-amber/10 font-medium text-navy-deep"
                : "border-cream-border text-taupe hover:bg-cream"
            } ${saving ? "cursor-wait opacity-60" : ""}`}
          >
            {LANG_LABELS[lang]}
          </button>
        ))}
      </div>
      {toast ? (
        <div className="mt-2 rounded-md bg-amber/10 px-3 py-1.5 text-[12px] text-amber-dark">
          {toast}
        </div>
      ) : null}
    </Card>
  );
}
